import type { AuthInitialState, GigyaResponseType } from './types';
import { isTokenExpired, decodeToken, emptyDecodedToken } from './utils';
import {
  createSlice,
  PayloadAction,
  type CaseReducer,
  type Slice,
} from '@reduxjs/toolkit';
import cookieReader from 'js-cookie';
import type { User } from '@xarvio/frontend-core-graphql/schema/schemaTypes.generated';
import type { ErrorResponseType } from '../requestTypes';

type AuthCaseReducers = {
  resetAuthState: CaseReducer<AuthInitialState>;
  requestAuthStart: CaseReducer<AuthInitialState>;
  requestSuccessLogin: CaseReducer<AuthInitialState, PayloadAction<string>>;
  requestAuthSuccess: CaseReducer<AuthInitialState>;
  requestAuthFailure: CaseReducer<
    AuthInitialState,
    PayloadAction<ErrorResponseType>
  >;
  setIsRegistering: CaseReducer<AuthInitialState, PayloadAction<boolean>>;
  setAccountInfo: CaseReducer<
    AuthInitialState,
    PayloadAction<{ accountInfo: GigyaResponseType; userToken: string }>
  >;
  setUserInfo: CaseReducer<AuthInitialState, PayloadAction<User>>;
  setLoginToken: CaseReducer<AuthInitialState, PayloadAction<string>>;
  logout: CaseReducer<AuthInitialState>;
};

type AuthSlice = Slice<AuthInitialState, AuthCaseReducers, 'auth@'>;

const getInitialState = (): AuthInitialState => {
  const userToken =
    cookieReader.get(import.meta.env.VITE_SECURITY_TOKEN_NAME) || '';
  const decodedToken = decodeToken(userToken);

  return {
    loggedIn: !isTokenExpired(decodedToken),
    user: {
      token: userToken,
      decoded: decodedToken,
    },
    status: 'idle',
    error: {},
    isUserRegistering: false,
    accountInfo: undefined,
    userInfo: undefined,
  };
};

const emptyUser = {
  token: '',
  decoded: emptyDecodedToken,
};

const authSlice: AuthSlice = createSlice({
  name: 'auth@',
  initialState: getInitialState,
  reducers: {
    resetAuthState: () => getInitialState(),
    requestAuthStart(state) {
      state.status = 'loading';
    },
    requestSuccessLogin(state, { payload }) {
      cookieReader.set(import.meta.env.VITE_SECURITY_TOKEN_NAME, payload, {
        domain: import.meta.env.VITE_TOKEN_DOMAIN,
        sameSite: 'Strict',
      });

      state.status = 'success';
      state.loggedIn = true;
      state.error = {};
      state.user = {
        token: payload,
        decoded: decodeToken(payload),
      };
    },
    requestAuthSuccess(state) {
      state.status = 'success';
      state.error = {};
    },
    requestAuthFailure(state, { payload }) {
      state.status = 'failure';
      state.error = payload;
      state.user = emptyUser;
      state.loggedIn = false;
    },
    setIsRegistering(state, action: PayloadAction<boolean>) {
      state.isUserRegistering = action.payload;
    },
    setAccountInfo(
      state,
      action: PayloadAction<{
        accountInfo: GigyaResponseType;
        userToken: string;
      }>,
    ) {
      if (state.user.token !== action.payload.userToken) {
        return;
      }

      state.accountInfo = action.payload.accountInfo;
    },
    setUserInfo(state, action: PayloadAction<User>) {
      state.userInfo = action.payload;
    },
    setLoginToken(state, action: PayloadAction<string>) {
      state.loginToken = action.payload;
    },
    logout(state) {
      state.user = emptyUser;
      state.loggedIn = false;
      state.status = 'idle';
      state.error = {};
      state.isUserRegistering = false;
      state.accountInfo = undefined;
    },
  },
});

export default authSlice;
